import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Plus, Send, History, Pencil } from "lucide-react";
import LineFormDialog from "./LineFormDialog";
import { PayeeCell } from "./shared/PayeeCell";
import { EditHistoryDialog } from "./shared/EditHistoryDialog";
import { DeleteEntryDialog } from "./shared/DeleteEntryDialog";

export function money(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export const LINE_STATUS_BADGE = {
  Draft: "bg-slate-400",
  Submitted: "bg-blue-500",
  Approved: "bg-violet-500",
  "Sent Back": "bg-red-500",
  Paid: "bg-emerald-600",
};

// One Payment List's lines with per-line add/edit/remove/submit/resubmit —
// per-line action flags come from the backend (can_edit/can_remove/
// can_resubmit) so role and status gating is enforced in one place.
export default function PaymentListDetail({ listId, refData, onClose, onChanged }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lineTarget, setLineTarget] = useState(null);
  const [correctTarget, setCorrectTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    client
      .get(`/payment-desk/payment-lists/${listId}`)
      .then((res) => setData(res.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [listId]);

  const isCreator = data?.created_by === user?.employee_id;
  const draftCount = (data?.lines || []).filter((l) => l.status === "Draft").length;

  const submitList = async () => {
    setBusy(true);
    try {
      await client.post(`/payment-desk/payment-lists/${listId}/submit`);
      toast.success("Payment List submitted for Director approval");
      load();
      onChanged();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const resubmitLine = async (line) => {
    setBusy(true);
    try {
      await client.post(`/payment-desk/lines/${line.id}/resubmit`);
      toast.success("Line resubmitted for Director approval");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !data) {
    return (
      <Sheet open onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="payment-list-detail">
          <SheetHeader>
            <SheetTitle>{listId}</SheetTitle>
          </SheetHeader>
          <p className="text-sm text-slate-400 py-8 text-center">Loading...</p>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="payment-list-detail">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {data.list_id}
            <Badge className={data.submitted ? "bg-emerald-600" : "bg-slate-400"}>{data.submitted ? "Submitted" : "Draft"}</Badge>
          </SheetTitle>
          <SheetDescription>
            By {data.created_by_name} · {new Date(data.created_at).toLocaleString("en-IN")}
          </SheetDescription>
          <div className="flex gap-2 pt-2">
            {isCreator && !data.submitted && (
              <Button size="sm" data-testid="payment-list-add-line-button" onClick={() => setLineTarget("new")} className="bg-amber-600 hover:bg-amber-700">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Line
              </Button>
            )}
            {isCreator && !data.submitted && draftCount > 0 && (
              <Button size="sm" variant="outline" disabled={busy} data-testid="payment-list-submit-button" onClick={submitList}>
                <Send className="h-3.5 w-3.5 mr-1" /> Submit for Approval
              </Button>
            )}
          </div>
        </SheetHeader>

        <div className="pt-6">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <Table data-testid="payment-lines-table">
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Paid To</TableHead>
                  <TableHead>Bill</TableHead>
                  <TableHead>Attributed To</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.lines.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No lines yet.</TableCell></TableRow>
                ) : (
                  data.lines.map((line) => (
                    <TableRow key={line.id} data-testid={`payment-line-row-${line.id}`}>
                      <TableCell className="font-mono text-xs">{line.line_no}</TableCell>
                      <TableCell className="text-xs">{line.vendor_name || "—"}</TableCell>
                      <TableCell><PayeeCell line={line} /></TableCell>
                      <TableCell className="font-mono text-xs">
                        {line.vendor_bill_id || "—"}
                        {line.bill_amount_applied != null && <span className="block text-slate-400">applied {money(line.bill_amount_applied)}</span>}
                      </TableCell>
                      <TableCell className="text-xs">{line.site_label || line.equipment_label}</TableCell>
                      <TableCell className="text-xs">{line.category_name}</TableCell>
                      <TableCell>{money(line.amount)}</TableCell>
                      <TableCell>
                        <Badge className={LINE_STATUS_BADGE[line.status]}>{line.status}</Badge>
                        {line.status === "Sent Back" && line.sent_back_reason && (
                          <p className="text-xs text-red-600 mt-1" data-testid={`payment-line-sent-back-reason-${line.id}`}>"{line.sent_back_reason}"</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right space-x-1.5">
                        {(line.edit_history || []).length > 0 && (
                          <Button size="sm" variant="outline" data-testid={`payment-line-history-${line.id}`} onClick={() => setHistoryTarget(line)}>
                            <History className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {line.can_edit ? (
                          <Button size="sm" variant="outline" data-testid={`payment-line-edit-${line.id}`} onClick={() => setLineTarget(line)}>
                            Edit
                          </Button>
                        ) : line.can_correct ? (
                          <Button size="sm" variant="outline" data-testid={`payment-line-correct-${line.id}`} onClick={() => setCorrectTarget(line)}>
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                        ) : (
                          <span className="text-[10px] text-slate-400" data-testid={`payment-line-window-closed-${line.id}`}>Edit window closed</span>
                        )}
                        {line.can_delete && (
                          <Button size="sm" variant="destructive" disabled={busy} data-testid={`payment-line-delete-${line.id}`} onClick={() => setDeleteTarget(line)}>
                            Delete
                          </Button>
                        )}
                        {line.can_resubmit && (
                          <Button size="sm" disabled={busy} data-testid={`payment-line-resubmit-${line.id}`} onClick={() => resubmitLine(line)} className="bg-amber-600 hover:bg-amber-700">
                            Resubmit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </SheetContent>

      {correctTarget && (
        <LineFormDialog
          correctionMode
          line={correctTarget}
          refData={refData}
          onClose={() => setCorrectTarget(null)}
          onSaved={() => { setCorrectTarget(null); load(); }}
        />
      )}
      {historyTarget && <EditHistoryDialog line={historyTarget} onClose={() => setHistoryTarget(null)} />}
      {deleteTarget && (
        <DeleteEntryDialog
          title={`Delete line #${deleteTarget.line_no}?`}
          endpoint={`/payment-desk/lines/${deleteTarget.id}/delete`}
          details={[
            ["Payment List", listId],
            ["Paid To", `${deleteTarget.payee_type}${deleteTarget.payee_name ? ` — ${deleteTarget.payee_name}` : ""}`],
            ["Amount", money(deleteTarget.amount)],
            ["Status", deleteTarget.status],
          ]}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); load(); onChanged(); }}
        />
      )}
      {lineTarget && (
        <LineFormDialog
          listId={listId}
          line={lineTarget === "new" ? null : lineTarget}
          refData={refData}
          onClose={() => setLineTarget(null)}
          onSaved={() => {
            setLineTarget(null);
            load();
          }}
        />
      )}
    </Sheet>
  );
}
